<script setup>
import gdpr from '../../config/gdpr.js';
import unlessElementExists from "../../lib/utils/unlessElementExists.js";
import {Modal} from "../../lib/typhoon/index.js";
import {fadeHide} from "../../lib/animate/index.js";
import getFieldsValues from "../../lib/utils/getFieldsValues.js";
import {useCookie} from "@straylightagency/cookies";
import {useLocalStorage} from "@straylightagency/storage";
import {onMounted, ref} from "vue";

const {privacyPolicy} = defineProps(['privacy-policy']);

const modalEl = ref();
const formEl = ref();

const categories = gdpr.categories;

const default_choices = Object.fromEntries(
    Object.entries( categories ).map( ([name,values]) => [ name, values.default ] )
);

const [, setCookiesNotice] = useLocalStorage('cookies.bar', false );
const [cookiesSettings, setCookiesSettings] = useCookie('cookies_settings', default_choices );

function handleChoices(choices) {
    Object.entries( gdpr.categories ).forEach( ([name,values]) => {
        if ( choices[ name ] === true && values.apps !== null ) {

            values.apps.forEach( app => {
                if ( gdpr.apps[ app ] ) {
                    unlessElementExists( '#' + gdpr.apps[ app ].id, el => {
                        if ( el.tagName.toLowerCase() === 'script' ) {
                            if ( el.hasAttribute('data-src') ) {
                                el.onload = () => typeof gdpr.apps[ app ].callback === 'function' && gdpr.apps[ app ].callback( el );
                                el.type = el.getAttribute('data-type');
                                el.src = el.getAttribute('data-src');
                            } else {
                                const newScriptTag = document.createElement('script');
                                newScriptTag.innerHTML = el.innerHTML;
                                typeof gdpr.apps[ app ].callback === 'function' && gdpr.apps[ app ].callback( newScriptTag );
                                el.parentNode.insertBefore( newScriptTag, el );
                                el.parentNode.removeChild( el );
                            }
                        }

                        if ( el.tagName.toLowerCase() === 'iframe' ) {
                            typeof gdpr.apps[ app ].callback === 'function' && gdpr.apps[ app ].callback( el );
                            el.src = el.getAttribute('data-src');
                        }
                    } );
                }
            } );
        }
    } );

    Object.entries( choices ).forEach( ([name,value]) => {
        if ( value ) {
            formEl.value.querySelector('input[type=checkbox][name='+ name +']').setAttribute('checked', true );
        }
    } );
}

function saveChoices(choices) {
    setCookiesNotice( true );
    setCookiesSettings( choices, { secure: location.protocol === 'https:' } );

    handleChoices( choices );
}

function submitChoices() {
    saveChoices( getFieldsValues( formEl.value ) );

    unlessElementExists( '#cookiesNotice', notice => fadeHide( notice, 300 ) );

    Modal.getInstanceOrCreate( modalEl.value ).hide();
}

onMounted( () => {
    const choices = cookiesSettings.value;
    formEl.value.querySelector('input[type=checkbox]').setAttribute('checked', false );

    Object.entries( choices ).forEach( ([name,value]) => {
        if ( value ) {
            formEl.value.querySelector('input[type=checkbox][name='+ name +']').setAttribute('checked', true );
        }
    } );
})
</script>

<template>
    <div class="fixed modal inset-0 px-5 hidden overflow-auto z-[150]" id="cookiesModal" ref="modalEl">
        <form class="relative w-full max-w-[780px] mx-auto my-20 rounded-md bg-white p-5 sm:p-10" id="cookiesForm" ref="formEl" @submit.prevent="submitChoices">
            <p class="font-bold text-xl mb-5">
                Paramétre des cookies
            </p>

            <button type="button" title="Fermer la modal" aria-label="Fermer la modal" class="absolute right-5 top-5 p-1 -m-1" ty-modal="#cookiesModal" tabindex="-1">
                <i class="fas fa-times"></i>
            </button>

            <p class="mb-5 text-base">
                Nous utilisons des cookies ou technologies similaires pour analyser le site web, optimiser votre navigation et proposer du contenu personnalisé sur et en dehors de notre site web. Vous pouvez choisir d'accepter ou de refuser ces options. Pour plus d'informations sur les cookies, les données que nous utilisons, le traitement des données et nos entreprises partenaires, consultez notre
                <a :href="privacyPolicy" class="underline" tabindex="-1">politique de confidentialité</a>.
            </p>

            <ul class="mb-5" id="cookiesAccordion">
                <li class="flex flex-row row-nowrap mb-5">
                    <div class="w-10/12">
                        <button type="button" title="Cookies de Fonctionnalité" aria-label="Cookies de Fonctionnalité" class="relative text-left font-semibold mb-2.5" tabindex="-1"
                                ty-accordion="#requiredCookies" ty-accordion-parent="#cookiesAccordion" aria-controls="requiredCookies" aria-expanded="true" ty-accordion-open="true">
                            <i aria-hidden="true" class="fa-fw fas fa-angle-down absolute top-1"></i>
                            <span class="block pl-6">Cookies de Fonctionnalité</span>
                        </button>

                        <p class="pl-6" id="requiredCookies">
                            Les cookies obligatoires sont des cookies indispensables au fonctionnement d'un site web. Sans ces cookies strictement nécessaires, le site web ne peut pas fonctionner correctement.
                        </p>
                    </div>

                    <div class="w-2/12">
                        <input type="checkbox" disabled checked class="block ml-auto w-4 h-4" name="required">
                    </div>
                </li>

                <li class="flex flex-row row-nowrap">
                    <div class="w-10/12">
                        <button type="button" title="Cookies de Statistiques" aria-label="Cookies de Statistiques" class="relative text-left font-semibold mb-2.5" tabindex="-1"
                                ty-accordion="#analyticsCookies" ty-accordion-parent="#cookiesAccordion" aria-controls="#analyticsCookies" aria-expanded="false">
                            <i aria-hidden="true" class="fa-fw fas fa-angle-down absolute top-1"></i>
                            <span class="block pl-6">Cookies de Statistiques</span>
                        </button>

                        <p class="pl-6" id="analyticsCookies" style="display: none;">
                            Pour évaluer si notre site web offre une expérience utilisateur satisfaisante et répond à vos attentes, nous analysons le trafic et rapportons des statistiques d'utilisation du site sans identifier les visiteurs individuels. Nous collectons uniquement des statistiques anonymes sur nos visiteurs et nous utilisons des cookies pour capturer ces données.
                        </p>
                    </div>

                    <div class="w-2/12">
                        <input type="checkbox" class="block ml-auto w-4 h-4" name="analytics" tabindex="-1">
                    </div>
                </li>
            </ul>

            <div>
                <button type="submit" title="Confirmer" tabindex="-1"
                        class="block ml-auto text-base border rounded px-5 py-2 bg-transparent hover:bg-slate-200 transition-colors transition-150">Confirmer</button>
            </div>
        </form>
    </div>
</template>

<style scoped>

</style>